import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    ClickAwayListener,
    Paper,
    Popper,
    Typography,
    alpha,
} from "@mui/material";
import {
    CheckRounded,
    ChevronLeftRounded,
    ChevronRightRounded,
    ExpandMoreRounded,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import StatusDot from "../common/StatusDot";
import { colors } from "../../theme/colors";
import {
    MESSAGE_TYPES,
    useWebSocketStore,
} from "../../stores/websocketStore";
import { toast } from "../../universal/toast";

const MotionPaper = motion(Paper);

const DURATION_OPTIONS = [
    { value: "session", label: "For this session" },
    { value: "30m", label: "30 minutes" },
    { value: "1h", label: "1 hour" },
    { value: "4h", label: "4 hours" },
    { value: "1d", label: "1 day" },
    { value: "permanent", label: "Permanent" },
];

function resolveStatusMeta(displayPresence, nowMs) {
    const expiresAt = displayPresence?.expiresAt;
    const expired = expiresAt && new Date(expiresAt).getTime() <= nowMs;
    return { offline: displayPresence?.visibility === "offline" && !expired };
}

function SelectorRow({ active, label, onClick, trailing }) {
    return (
        <Box
            component="button"
            type="button"
            role="menuitemradio"
            aria-checked={active}
            onClick={onClick}
            sx={{
                width: "100%",
                minHeight: "2.55rem",
                display: "flex",
                alignItems: "center",
                gap: "0.65rem",
                px: "0.72rem",
                py: "0.48rem",
                color: colors.text.primary,
                border: active
                    ? `1px solid ${alpha(colors.primary.main, 0.24)}`
                    : "1px solid transparent",
                borderRadius: "0.7rem",
                background: active
                    ? `linear-gradient(100deg, ${alpha(colors.primary.main, 0.1)}, rgba(255,255,255,0.025))`
                    : "transparent",
                font: "inherit",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 170ms ease",
                "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.055)",
                    borderColor: "rgba(255,255,255,0.065)",
                },
                "&:focus-visible": {
                    outline: `2px solid ${alpha(colors.primary.main, 0.65)}`,
                    outlineOffset: "1px",
                },
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.78rem", fontWeight: 750, lineHeight: 1.2 }}>
                    {label}
                </Typography>
            </Box>
            {active ? (
                <CheckRounded sx={{ color: colors.primary.main, fontSize: "1rem" }} />
            ) : trailing}
        </Box>
    );
}

SelectorRow.propTypes = {
    active: PropTypes.bool,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    trailing: PropTypes.node,
};

export default function DisplayStatusLabel({ displayPresence }) {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [view, setView] = useState("status");
    const [pending, setPending] = useState(false);
    const [nowMs, setNowMs] = useState(Date.now());
    const { sendOwnerAction, subscribe } = useWebSocketStore();

    const statusMeta = useMemo(
        () => resolveStatusMeta(displayPresence, nowMs),
        [displayPresence, nowMs],
    );

    useEffect(() => {
        if (!displayPresence?.expiresAt) return undefined;
        const interval = window.setInterval(() => setNowMs(Date.now()), 30000);
        return () => window.clearInterval(interval);
    }, [displayPresence?.expiresAt]);

    useEffect(() => {
        if (!pending) return undefined;
        const unsubscribe = subscribe(({ message }) => {
            if (message?.messageId === MESSAGE_TYPES.DISPLAY_STATUS_UPDATED) {
                setPending(false);
                setOpen(false);
                setView("status");
            } else if (message?.messageId === MESSAGE_TYPES.DISPLAY_STATUS_ERROR) {
                setPending(false);
            }
        });
        const timeout = window.setTimeout(() => {
            setPending(false);
            toast.error("Display status update timed out");
        }, 10000);
        return () => {
            unsubscribe();
            window.clearTimeout(timeout);
        };
    }, [pending, subscribe]);

    function submitStatus(status, duration) {
        if (pending) return;
        setPending(true);
        sendOwnerAction({
            messageId: MESSAGE_TYPES.SET_DISPLAY_STATUS,
            status,
            ...(duration ? { duration } : {}),
        });
    }

    function handleToggle(event) {
        event.preventDefault();
        event.stopPropagation();
        setOpen((value) => !value);
        setView("status");
    }

    function handleClose() {
        if (!pending) {
            setOpen(false);
            setView("status");
        }
    }

    function handleSelectorKeyDown(event) {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            handleClose();
        }
    }

    const selectedDuration = statusMeta.offline ? displayPresence?.duration : null;

    return (
        <ClickAwayListener
            mouseEvent="onMouseDown"
            touchEvent="onTouchStart"
            onClickAway={handleClose}
        >
        <Box sx={{ position: "relative", display: "inline-flex", maxWidth: "100%" }}>
            <Box
                ref={anchorRef}
                component="button"
                type="button"
                aria-label={`Display status: ${statusMeta.offline ? "Offline" : "Online"}`}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={handleToggle}
                sx={{
                    maxWidth: "100%",
                    minHeight: "1.72rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.34rem",
                    px: "0.48rem",
                    py: "0.32rem",
                    borderRadius: "0.55rem",
                    border: 0,
                    color: statusMeta.offline
                        ? alpha(colors.text.primary, 0.58)
                        : alpha(colors.text.primary, 0.88),
                    background: `
                        linear-gradient(
                            165deg,
                            rgba(18,18,22,0.92) 0%,
                            rgba(12,12,14,0.96) 52%,
                            rgba(10,10,12,0.98) 100%
                        )
                    `,
                    boxShadow:
                        "inset 0 1px 0 rgba(255,255,255,0.04), 0 5px 14px rgba(0,0,0,0.14)",
                    font: "inherit",
                    cursor: "pointer",
                    transition: "all 180ms ease",
                    "&:hover": {
                        color: alpha(colors.text.primary, 0.98),
                        background: `
                            linear-gradient(
                                165deg,
                                rgba(24,24,29,0.94) 0%,
                                rgba(16,16,19,0.97) 55%,
                                rgba(12,12,15,0.99) 100%
                            )
                        `,
                        transform: "translateY(-1px)",
                    },
                    "&:focus-visible": {
                        outline: `2px solid ${alpha(colors.primary.main, 0.7)}`,
                        outlineOffset: "2px",
                    },
                }}
            >
                <StatusDot
                    online={!statusMeta.offline}
                    variant="inline"
                    animateGlow={false}
                    sx={{ fontSize: "0.55rem" }}
                />
                <Typography
                    component="span"
                    sx={{
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: { xs: "0.61rem", md: "0.67rem" },
                        fontWeight: 800,
                        lineHeight: 1,
                        letterSpacing: "0.09em",
                        textTransform: "uppercase",
                    }}
                >
                    {statusMeta.offline ? "Offline" : "Online"}
                </Typography>
                <ExpandMoreRounded
                    sx={{
                        flexShrink: 0,
                        fontSize: "0.9rem",
                        color: alpha(colors.text.primary, 0.46),
                        transform: open ? "rotate(180deg)" : "none",
                        transition: "transform 180ms ease",
                    }}
                />
            </Box>

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                sx={{ zIndex: 10020 }}
                modifiers={[
                    { name: "offset", options: { offset: [0, 8] } },
                    { name: "flip", enabled: false },
                    {
                        name: "preventOverflow",
                        options: { padding: 8, tether: false },
                    },
                ]}
            >
                    <MotionPaper
                        role="menu"
                        initial={{ opacity: 0, y: -5, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={handleSelectorKeyDown}
                        sx={{
                            width: "200px",
                            overflow: "hidden",
                            p: "0.42rem",
                            borderRadius: "0.95rem",
                            border: "1px solid rgba(255,255,255,0.075)",
                            background: "linear-gradient(180deg, rgba(18,18,21,0.985), rgba(9,9,11,0.99))",
                            boxShadow: "0 24px 60px rgba(0,0,0,0.58), inset 0 1px 0 rgba(255,255,255,0.045)",
                            backdropFilter: "blur(24px)",
                        }}
                    >
                        <Box>
                            {view === "status" ? (
                                <Box>
                                    <Typography sx={{ px: "0.7rem", py: "0.48rem", color: colors.text.muted, fontSize: "0.61rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                                        Display status
                                    </Typography>
                                    <SelectorRow
                                        active={!statusMeta.offline}
                                        label="Online"
                                        onClick={() => submitStatus("online")}
                                    />
                                    <SelectorRow
                                        active={statusMeta.offline}
                                        label="Appear offline"
                                        onClick={() => setView("duration")}
                                        trailing={<ChevronRightRounded sx={{ color: colors.text.muted, fontSize: "1.05rem" }} />}
                                    />
                                </Box>
                            ) : (
                                <Box>
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => setView("status")}
                                        sx={{ display: "flex", alignItems: "center", gap: "0.25rem", width: "100%", px: "0.48rem", py: "0.42rem", color: colors.text.secondary, border: 0, background: "transparent", font: "inherit", cursor: "pointer" }}
                                    >
                                        <ChevronLeftRounded sx={{ fontSize: "1rem" }} />
                                        <Typography sx={{ fontSize: "0.63rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                                            Appear offline for
                                        </Typography>
                                    </Box>
                                    {DURATION_OPTIONS.map((option) => (
                                        <SelectorRow
                                            key={option.value}
                                            active={selectedDuration === option.value}
                                            label={option.label}
                                            onClick={() => submitStatus("offline", option.value)}
                                        />
                                    ))}
                                    {pending ? (
                                        <Typography sx={{ px: "0.7rem", py: "0.38rem", color: colors.text.muted, fontSize: "0.64rem" }}>
                                            Updating status…
                                        </Typography>
                                    ) : null}
                                </Box>
                            )}
                        </Box>
                    </MotionPaper>
            </Popper>
        </Box>
        </ClickAwayListener>
    );
}

DisplayStatusLabel.propTypes = {
    displayPresence: PropTypes.shape({
        visibility: PropTypes.oneOf(["online", "offline"]),
        duration: PropTypes.oneOf(["session", "30m", "1h", "4h", "1d", "permanent"]),
        selectedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    }),
};
